import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AssessmentResult, AssessmentResultsCode, AssessmentStatus, Note } from "@store/generalTypes";

const emptyAssessmentResult: AssessmentResult = {
    assessmentFormId: 0,
    assessmentFormName: "",
    assessmentId: 0,
    clinicianFirstName: "",
    clinicianId: "",
    clinicianLastName: "",
    displayId: "",
    examineeId: 0,
    facilitatorFirstName: "",
    facilitatorId: "",
    facilitatorLastName: "",
    id: 0,
    languageId: 0,
    languageName: "",
    launchedDate: "",
    locationId: 0,
    locationName: "",
    responses: [],
    result: {
        pieRatio: {
            name: "",
            score: 0,
            status: AssessmentResultsCode.Discontinued,
            impairedRange: "",
            marginalRange: "",
            normalRange: "",
            normalThreshold: 0
        },
        totalLearningScore: {
            name: "",
            score: 0,
            status: AssessmentResultsCode.Discontinued,
            normalRange: "",
            uninterpretableRange: ""
        },
        trialScores: []
    },
    status: AssessmentStatus.Incomplete,
    visitNumber: 0
};

interface TargetResponseNote {
    id: number;
    note: Note;
}

export const initialState = emptyAssessmentResult;

const assessmentResultSlice = createSlice({
    name: "assessmentResult",
    initialState,
    reducers: {
        setAssessmentResults(state, action: PayloadAction<AssessmentResult>) {
            state.assessmentFormId = action.payload.assessmentFormId;
            state.assessmentFormName = action.payload.assessmentFormName;
            state.assessmentId = action.payload.assessmentId;
            state.clinicianFirstName = action.payload.clinicianFirstName;
            state.clinicianId = action.payload.clinicianId;
            state.clinicianLastName = action.payload.clinicianLastName;
            state.displayId = action.payload.displayId;
            state.examineeId = action.payload.examineeId;
            state.facilitatorFirstName = action.payload.facilitatorFirstName;
            state.facilitatorId = action.payload.facilitatorId;
            state.facilitatorLastName = action.payload.facilitatorLastName;
            state.id = action.payload.id;
            state.languageId = action.payload.languageId;
            state.languageName = action.payload.languageName;
            state.launchedDate = action.payload.launchedDate;
            state.isRemote = action.payload.isRemote;
            state.locationId = action.payload.locationId;
            state.locationName = action.payload.locationName;
            state.responses = action.payload.responses;
            state.result = action.payload.result;
            state.status = action.payload.status;
            state.visitNumber = action.payload.visitNumber;
            state.assessmentCount = action.payload.assessmentCount;
            state.notes = action.payload.notes;
        },
        appendTargetResponseNote(state, action: PayloadAction<TargetResponseNote>) {
            const response = state.responses.find((x) => x.responses.some((r) => r.id === action.payload.id));

            if (response) {
                const targetResponse = response?.responses.find((x) => x.id === action.payload.id);

                if (targetResponse) {
                    targetResponse?.notes?.push(action.payload.note);
                }
            }
        },
        appendAssessmentNote(state, action: PayloadAction<Note>) {
            state.notes?.push(action.payload);
        }
    }
});

export const { setAssessmentResults, appendTargetResponseNote, appendAssessmentNote } = assessmentResultSlice.actions;

export default assessmentResultSlice.reducer;
